import { Formik, useFormikContext } from "formik";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Helmet from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { Loadable, PopoverLink } from '../../common';
import { ImageUploader } from '../../common/ImageUploader';
import { RichTextEditor } from '../../common/RichTextEditor';
import { FormControl, InputLabel, Select, MenuItem, Typography } from "@mui/material";
import { infoToastify } from "../../util/toast";
import { ContractsSearchComponent } from '../contractsPage/ContractsSearchComponent';
import { CONTRACT_STATE_FINALIZED } from '../../store/constants/contract';

import {
    Portlet,
    PortletBody,
    PortletHeader,
    PortletHeaderToolbar
} from "../../partials/content/Portlet";

import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    Switch,
    Box,
    TextField,
    Autocomplete
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { ZoomInOutlined } from "@mui/icons-material";
import html2canvas from "html2canvas";
import { PreviewContent, PreviewContent as PreviewContentCopy } from "./PreviewContent";
import { SignatureLabelsArray } from "./SignatureLabelsArray";

const useStyles = makeStyles(theme => ({
    button: {
        margin: theme.spacing(1),
    },
    root: {
        flexGrow: 1,
    },
    textField: {
        margin: theme.spacing(1),
        width: '80%'
    },
    label: {
        margin: theme.spacing(1),
    },
    switch: {
        margin: theme.spacing(1),
    },
    cardTop: {
        marginTop: theme.spacing(1),
    },
    textArea: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: '80%',
    },
    checkbox: {
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(0),
    },
    helpClass: {
        fontSize: '13px'
    },
    disclaimerCard: {
        paddingTop: '0px'
    },
    blurryPreview: {
        width: '200px',
        height: '150px',
        position: 'relative',
        cursor: 'pointer',
        border: '3px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '4px',
        margin: '10px 2px',
        display: 'grid',
        alignItems: 'center'
    },
    maximizeIcon: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        cursor: 'pointer',
        transform: 'translate(-50%, -62%)',
        opacity: '1',
        // transition: 'opacity 0.3s',
        "&:hover": {
                opacity: '1',
            },
    },
    previewImg: {
        width: '190px'
    },
    previewRefDiv: {
        position : 'absolute', top: '-10000px'
    }
    
}));

export default function ClientBrandingPage(props) {

    const {
        initialValues, intl, mode, clientId,facilityId, tinyContracts, 
        getBrandingPending, createBrandingPending, updateBrandingPending, brand, pricerPayerContracts,
        actions: { getFacilityBranding, createFacilityBranding, updateFacilityBranding,getBrand,getPricerPayerContractsTiny, getTinyContractsByState}
    } = props;

    const history = useHistory();
    const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    const [disclaimerType, setDisclaimerType] = useState("");

    useEffect(() => {
        if (facilityId) {
            getFacilityBranding(facilityId);
        }
    }, [facilityId]);

    useEffect(() => {
        getBrand();
    }, [getBrand]);

    useEffect(() => {
            getTinyContractsByState(CONTRACT_STATE_FINALIZED);
        }, [getTinyContractsByState]);

    useEffect(() => {
    if (initialValues?.outpatientDisclaimer) {
        setDisclaimerType("outpatient");
    } else if (initialValues?.professionalDisclaimer) {
        setDisclaimerType("professional");
    } else if (initialValues?.inpatientDisclaimer) {
        setDisclaimerType("inpatient");
    }
}, [initialValues]);

    const [file, setFile] = useState(null);
    const [thumbnailSrc, setThumbnailSrc] = useState(null);
    const previewRef = useRef(null);
    const classes = useStyles();

    const handleFileChange = (event) => {
        setFile(event.target.files[0]);
    }

    const handleDisclaimerTypeChange = (event) => {
        setDisclaimerType(event.target.value);
    };

    function handlePreviewDialogOpen() {
        setOpenPreviewDialog(true);
    }

    function handlePreviewDialogClose() {
        setOpenPreviewDialog(false);
    }

    function hoverColumn(text) {
        return (
            <PopoverLink
                columnData={<i className={`${classes.helpIcon} fas fa-question-circle text-info`}></i>}
                hoverData={[
                    {
                        data: text
                    }
                ]}
            />
        );
    }

    const generateThumbnail = useCallback(debounce(() => {
        
        if(previewRef.current === null) { return; }

        html2canvas(previewRef.current, {useCORS: true, scale: 0.5})
            .then((canvas) => {
                
                const dataUrl = canvas.toDataURL('image/png');
                setThumbnailSrc(dataUrl);
        })
        .catch((err) => {
            console.log('Error capturing thumbnail:', err);
        });

    }, 1000), []);

    const FormObserver = ({ onValuesChange }) => {
        const { values } = useFormikContext();

        useEffect(() => {
            if(onValuesChange) {
                onValuesChange(values);
            }
        }, [values, onValuesChange]);

        return null;

    }

    return (<>
        <Loadable loading={getBrandingPending || createBrandingPending || updateBrandingPending }>
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validate={values => {
            const errors = {};
             if (values.isPromptPayEnabled) {
                if (!values.promptPayPercentage && values.promptPayPercentage !== 0) {
                    errors.promptPayPercentage = "Percentage is required";
                } else if (values.promptPayPercentage < 0 || values.promptPayPercentage > 100) {
                    errors.promptPayPercentage = "Must be between 0 and 100";
                }

                if (!values.promptPayDays && values.promptPayDays !== 0) {
                    errors.promptPayDays = "Days are required";
                } else if (values.promptPayDays <= 0) {
                    errors.promptPayDays = "Must be greater than 0";
                }
            }

            // 🔹 Self Pay Validation
            if (values.isSelfPayEnabled) {
                if (!values.selfPayPercentage && values.selfPayPercentage !== 0) {
                    errors.selfPayPercentage = "Percentage is required";
                } else if (values.selfPayPercentage < 0 || values.selfPayPercentage > 100) {
                    errors.selfPayPercentage = "Must be between 0 and 100";
                }
            }

                return errors;
            }}
            onSubmit={(values) => {
                
                let payload = {
                    ClientId: clientId,
                    logoPath: values.logoPath,
                    usePricerLogo: values.usePricerLogo ?? false,
                    signatureLabelList: values.signatureLabels.filter(x=> x !== '') ?? [],
                    outpatientDisclaimer: values.outpatientDisclaimer,
                    professionalDisclaimer: values.professionalDisclaimer,
                    inpatientDisclaimer: values.inpatientDisclaimer,
                    isPromptPayEnabled: values.isPromptPayEnabled,
                    isSelfPayEnabled: values.isSelfPayEnabled,
                    promptPayPercentage: values.promptPayPercentage ? Number(values.promptPayPercentage) : null,
                    promptPayDays: values.promptPayDays ? Number(values.promptPayDays) : null,
                    selfPayPercentage: values.selfPayPercentage ? Number(values.selfPayPercentage) : null,
                    selfPayContractId: values.selfPayContract?.id || null,   
                };
                
                const formData = new FormData();
                
                if(payload.usePricerLogo) {
                    formData.append('File', null);
                }
                else if (file) {
                    formData.append('File', file);
                    payload.logoPath = '';
                }
                formData.append('Payload', JSON.stringify(payload));

                const callback = () => {
                    infoToastify(
                        <>
                            <b>Configuration Saved</b>
                            <br />
                            Your changes have been saved successfully.
                        </>
                    );
                    history.push('/configuration-branding/branding');
                };

                    if (mode === 'create' || !values.id) {
                        createFacilityBranding(facilityId, formData, callback);
                    } else {
                        updateFacilityBranding(facilityId, formData, callback);
                    }
                }}
            >
                {({
                    values,
                    errors,
                    touched,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue,
                setValues,
                }) => {

                    const selectedContract = {id: values?.selfPayContract?.id, name:'test'};
                    return (
                        <form onSubmit={handleSubmit} noValidate autoComplete="off" className={classes.container}>
                            <Helmet title="Branding Configurations" />
                            <Portlet>
                                <PortletHeader
                                    title={'Branding'}
                                    titleComponent={
                                        facilityId ? (
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    fontWeight: 500,  
                                                    fontSize: "1.25rem",
                                                    color: "inherit",
                                                    lineHeight: 1
                                                }}
                                            >
                                                {initialValues?.facilityName || "Facility Branding"}
                                            </Typography>
                                        ) : null
                                    }
                                    toolbar={
                                        <PortletHeaderToolbar>
                                         <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={createBrandingPending || updateBrandingPending}
                            >
                            Save
                            </Button>
                        </PortletHeaderToolbar>
                        }
                    />
                    <PortletBody>
                        <div className={classes.root}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8} lg={8}>
                                <Card variant="outlined">
                                    <CardHeader title={<div>Branding Logo <span className={classes.helpClass}> {hoverColumn('This branding logo will be used in estimation history page.')} </span> </div>} />
                                    <CardContent>
                                        <ImageUploader 
                                            logoPath={values.logoPath}
                                            resizedImage={file}
                                            setResizedImage={setFile}
                                            disabled={values.usePricerLogo}
                                            pricerBucket={values.pricerBucket}
                                            brandLogoPath={brand?.logoPath ?? ''}
                                        />
                                        <FormControlLabel
                                            control={
                                            <Switch
                                                name="usePricerLogo"
                                                checked={values.usePricerLogo}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className={classes.switch}
                                                value={values.usePricerLogo}
                                            />
                                            }
                                            label={<div>Use Pricer Logo <span className={classes.helpClass}> {hoverColumn('Import the pricer logo from pricer tool.')} </span> </div>} 
                                        />
                                    </CardContent>
                                </Card>
                                <br />
                                <Card variant="outlined" className={classes.cardTop}>
                                <CardHeader title="Payment Configuration" />
                                <CardContent>

                                <Grid container alignItems="center" spacing={2} mt={1}>

                                    <Grid item xs={3}>
                                        <FormControlLabel
                                        control={
                                            <Switch
                                            checked={values.isPromptPayEnabled}
                                            onChange={(e, checked) => {
                                                setFieldValue('isPromptPayEnabled', checked);

                                                if (!checked) {
                                                setFieldValue('promptPayPercentage', null);
                                                setFieldValue('promptPayDays', null);
                                                }
                                            }}
                                            />
                                        }
                                        label="Prompt Pay Discount"
                                        />
                                    </Grid>

                                    <Grid item xs={3} mb={2}>
                                        {values.isPromptPayEnabled && (
                                        <TextField
                                            fullWidth
                                            label="Percentage*"
                                            type="number"
                                            value={values.promptPayPercentage}
                                            error={touched.promptPayPercentage && Boolean(errors.promptPayPercentage)}
                                            helperText={touched.promptPayPercentage && errors.promptPayPercentage}
                                            onChange={(e) => setFieldValue('promptPayPercentage', e.target.value)}
                                        />
                                        )}
                                    </Grid>

                                    <Grid item xs={3} mb={2}>
                                        {values.isPromptPayEnabled && (
                                        <TextField
                                            fullWidth
                                            label="Days*"
                                            type="number"
                                            value={values.promptPayDays}
                                            error={touched.promptPayDays && Boolean(errors.promptPayDays)}
                                            helperText={touched.promptPayDays && errors.promptPayDays}
                                            onChange={(e) => setFieldValue('promptPayDays', e.target.value)}
                                        />
                                        )}
                                    </Grid>

                                    </Grid>

                                     <Grid container alignItems="center" spacing={2} mt={1}>

                                    <Grid item xs={3}>
                                        <FormControlLabel
                                        control={
                                            <Switch
                                            checked={values.isSelfPayEnabled}
                                            onChange={(e, checked) => {
                                                setFieldValue('isSelfPayEnabled', checked);

                                            if (!checked) {
                                                setFieldValue('selfPayContract', null);
                                                setFieldValue('selfPayPercentage', null);
                                            }
                                            }}
                                            />
                                        }
                                        label="Self Pay"
                                        />
                                    </Grid>

                                    {/* Percentage */}
                                    <Grid item xs={3} mb={2}>
                                        {values.isSelfPayEnabled && (
                                        <TextField
                                            fullWidth
                                            label="Percentage*"
                                            type="number"
                                            value={values.selfPayPercentage}
                                            error={touched.selfPayPercentage && Boolean(errors.selfPayPercentage)}
                                            helperText={touched.selfPayPercentage && errors.selfPayPercentage}
                                            onChange={(e) => setFieldValue('selfPayPercentage', e.target.value)}
                                        />
                                        )}
                                    </Grid>
                                    {/* Contract */}
                                    <Grid item xs={3} mb={3}>
                                        {values.isSelfPayEnabled && (
                                            <>
                                                <TextField
                                                    fullWidth
                                                    label="Contract"
                                                    value={values.selfPayContract?.name || ""}
                                                    margin="normal"
                                                    onClick={() => {
                                                        document.getElementById("hidden-contract-trigger")?.click();
                                                    }}
                                                    InputProps={{
                                                        readOnly: true,
                                                        endAdornment: values.selfPayContract ? (
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();

                                                            setFieldValue(
                                                                "selfPayContract",
                                                                null
                                                            );
                                                        }}
                                                            >
                                                                ✕
                                                            </IconButton>
                                                        ) : null
                                                    }}
                                                />

                                                
                                            </>
                                        )}

                                        {/* Hidden ContractsSearchComponent */}
                                        <div style={{ display: "none" }}>
                                            <ContractsSearchComponent
                                                value={values.selfPayContract?.name || ""}
                                                handleSetContract={(value) => {
                                                    setFieldValue(
                                                        "selfPayContract",
                                                        value
                                                    );

                                                }}
                                                icon={
                                                    <button type="button" id="hidden-contract-trigger">
                                                        open
                                                    </button>
                                                }
                                                overrideSelfPayRestriction={true}
                                            />
                                        </div>
                                    </Grid>
                                    {values.isSelfPayEnabled && !values.selfPayContract && (
                                        <Grid container spacing={2}>
                                            <Grid item xs={3}></Grid>
                                            <Grid item xs={3}></Grid>

                                            <Grid item xs={3}>
                                                <Box mt={-2} sx={{ paddingLeft: "9px" }}>
                                                    <small style={{ color: "#666" }}>
                                                        No contract selected - Self pay will be based on Percent of Charge
                                                    </small>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Grid>
                                   
                                </CardContent>
                                </Card>
                                <br />
                                <Card variant="outlined">
                                    <CardHeader title={<div>Disclaimer <span className={classes.helpClass}> {hoverColumn('This disclaimer text will be used in estimation history page.')} </span> </div>} />
                                    <CardContent className={classes.disclaimerCard}>
                                        <FormControl fullWidth style={{ margin: "8px 0", width: "320px" }}>
                                            <InputLabel id="disclaimer-type-label">
                                                Select Disclaimer Type
                                            </InputLabel>

                                            <Select
                                                labelId="disclaimer-type-label"
                                                value={disclaimerType}
                                                onChange={handleDisclaimerTypeChange}
                                                label="Select Disclaimer Type"
                                            >
                                                <MenuItem value="professional">Professional Disclaimer</MenuItem>
                                                <MenuItem value="outpatient">Outpatient Disclaimer</MenuItem>
                                                <MenuItem value="inpatient">Inpatient Disclaimer</MenuItem>
                                            </Select>
                                        </FormControl>

                                        <RichTextEditor
                                            key={disclaimerType}
                                            value={
                                                disclaimerType === "outpatient"
                                                    ? values.outpatientDisclaimer
                                                    : disclaimerType === "professional"
                                                    ? values.professionalDisclaimer
                                                    : disclaimerType === "inpatient"
                                                    ? values.inpatientDisclaimer
                                                    : ""
                                            }
                                            fieldName={
                                                disclaimerType === "outpatient"
                                                    ? "outpatientDisclaimer"
                                                    : disclaimerType === "professional"
                                                    ? "professionalDisclaimer"
                                                    : disclaimerType === "inpatient"
                                                    ? "inpatientDisclaimer"
                                                    : ""
                                            }
                                            setFieldValue={setFieldValue}
                                            disabled={!disclaimerType}
                                        />

                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={8} lg={4}>
                                <Card variant="outlined" >
                                    <CardHeader title={<div>Signature Labels <span className={classes.helpClass}> {hoverColumn('These signature labels will be used in estimation history page.')} </span> </div>}
                                    />
                                    <CardContent>
                                        <SignatureLabelsArray
                                            values={values}
                                            handleChange={handleChange}
                                            handleBlur={handleBlur}
                                            touched={touched}
                                            errors={errors}
                                            setFieldValue={setFieldValue}
                                        />
                                    </CardContent>
                                </Card>
                                <Grid className={classes.blurryPreview} onClick={handlePreviewDialogOpen}>
                                    
                                    {/* <Skeleton className={`${classes.marginAuto} my-2`} variant="circular" width={40} height={40} />
                                    <Skeleton className={`mx-2 my-2`} variant="rounded" width={180} height={60} />
                                    <Skeleton className={`${classes.marginAuto} my-2`} variant="rounded" width={80} height={10} />
                                    <Skeleton className={`${classes.marginAuto} my-2`} variant="rounded" width={80} height={10} />
                                    <Skeleton className={`${classes.marginAuto} my-2`} variant="rounded" width={80} height={10} />
                                    */}

                                    <img src={thumbnailSrc} alt='Preview' className={classes.previewImg} />
                                    <IconButton className={classes.maximizeIcon} size="large">
                                        <ZoomInOutlined fontSize='large' /> Preview
                                    </IconButton>
                                </Grid>
                                <FormObserver onValuesChange={generateThumbnail} />
                            </Grid>
                        </Grid>
                        </div>
                    </PortletBody>
                    </Portlet>
                    <Dialog maxWidth='md' fullWidth open={openPreviewDialog} onClose={handlePreviewDialogClose}>
                        <DialogTitle className='py-3'>
                            Preview
                        </DialogTitle>
                        <DialogContent dividers >
                            <PreviewContent values={values} file={file} brand={brand} disclaimerType={disclaimerType} />
                        </DialogContent>
                        <DialogActions className='p-4'>
                            <Button variant="contained" onClick={handlePreviewDialogClose}>
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <div ref={previewRef} className={classes.previewRefDiv}>
                        <PreviewContentCopy values={values} file={file} brand={brand} disclaimerType={disclaimerType} />
                    </div>
                </form>
                 );
                }}
         </Formik>
         </Loadable>
     </>);

}
